(ns client-portal.client.asr-training.views
  (:require [clojure.string :as s]
            [client-portal.client.asr-training.data :as asr-training.d]
            [client-portal.client.audio.views :as audio]
            [client-portal.client.layout :as l]
            [client-portal.client.smart-ordering.filters.views :as so.filters]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.components.components :as c]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(def cmd-key (if (util/mac?) "⌘" "Ctrl"))

(defn shortcut-tooltip [label & ks]
  (into [:div {:style {:display :flex
                       :align-items :center
                       :gap 4}}
         (when label
           [:span {:style {:margin-right 4}}
            label])]
        (map (fn [k]
               [:kbd {:style {:background-color (style/mix-color (style/alc-color "Common/White") 15)
                              :border (str "1px solid " (style/mix-color (style/alc-color "Common/White") 30))
                              :border-radius 999
                              :font-family :inherit
                              :font-size 11
                              :line-height "16px"
                              :padding "1px 8px"}}
                k])
             ks)))

(defn submit-config [edited?]
  (if edited?
    {:label "Update"
     :icon-type :EditIcon
     :button-style {:background-color (style/alc-color "Background/alert_const")
                    :border-color (style/alc-color "Chart/orange")
                    :color (style/alc-color "Text/primary_const")}}
    {:label "Approve"
     :icon-type :LikeIcon
     :button-style {:background-color (style/alc-color "Background/success_const")
                    :border-color (style/alc-color "Common/success")
                    :color (style/alc-color "Text/primary_const")}}))

(defn transcript-key-down [product utterance]
  (fn [e]
    (when-let [event (and (if (util/mac?) (.-metaKey e) (.-ctrlKey e))
                          (case (s/lower-case (.-key e))
                            "enter" :asr-training/submit-utterance
                            "j" :asr-training/skip-utterance
                            nil))]
      (.preventDefault e)
      (re/dispatch [event product utterance]))))

(defn audio-unavailable []
  [:span {:style {:display :flex
                  :align-items :center
                  :gap 4
                  :flex "0 0 auto"
                  :color (style/alc-color "Text/secondary")}}
   [alc/up-secondary-icon {:icon-type :VolumeOffIcon
                           :style {:font-size 14}}]
   "Audio unavailable"])

(defn table-args [product]
  (let [has-more? @(re/subscribe [:asr-training/has-more?- product])
        next-page @(re/subscribe [:asr-training/next-page- product])]
    {:no-data-text "No utterances to display"
     :loading? @(re/subscribe [:asr-training/list-loading?- product])
     :loading-more? @(re/subscribe [:asr-training/next-page-loading?- product])
     :scroll-container-id l/core-scroll-content-id
     :bottom-px-buffer 200
     :on-bottom-buffer (when has-more?
                         #(re/dispatch [:asr-training/load-utterances [nil {:product product
                                                                            :page next-page}]]))
     :has-more-rows? true
     :sticky-headers? true
     :header-top @(re/subscribe [:asr-training/header-height- product])
     :row-style {:height :auto
                 :align-items :center}}))

(defn utterances-table [{:keys [product]}]
  (let [utterances- (re/subscribe [:asr-training/utterances- product])
        audio- (re/subscribe [:asr-training/audio- product])]
    (re/create-class
      {:component-did-mount #(re/dispatch [:asr-training/prefetch-audio product nil])
       :reagent-render
       (fn [{:keys [product]}]
         (let [selected-key (some-> @audio- :utterance (asr-training.d/utterance-key))]
           [alc/table (merge (table-args product)
                             {:flex-vals [1]
                              :headers ["Original Transcript"]
                              :rows (map
                                      (fn [{:keys [transcript audio-url] :as utterance}]
                                        (let [k (asr-training.d/utterance-key utterance)
                                              selected? (= selected-key k)]
                                          {:key k
                                           :cells [{:label (if audio-url
                                                             transcript
                                                             [:div {:style {:display :flex
                                                                            :align-items :center
                                                                            :gap 12
                                                                            :width "100%"
                                                                            :color (style/alc-color "Text/secondary")}}
                                                              [:span {:style {:flex 1}}
                                                               transcript]
                                                              [audio-unavailable]])
                                                    :style {:padding "0 12px"}}]
                                           :on-click (when audio-url
                                                       #(re/dispatch (if selected?
                                                                       [:asr-training/toggle-play product]
                                                                       [:asr-training/select-utterance product utterance])))
                                           :style {:cursor (if audio-url :pointer :default)
                                                   :opacity (if (and selected-key (not selected?)) 0.3 1)}}))
                                      @utterances-)})]))})))

(defn list-player [{:keys [product utterance selected?]}]
  (let [{:keys [loading? playing? error?]} (when selected?
                                             @(re/subscribe [:asr-training/audio- product]))]
    (if (or error? (not (:audio-url utterance)))
      [audio-unavailable]
      [audio/player-bar {:playing? playing?
                         :loading? loading?
                         :disabled? loading?
                         :position (if selected? @asr-training.d/position* 0)
                         :duration (asr-training.d/segment-duration utterance)
                         :on-toggle #(re/dispatch (if selected?
                                                    [:asr-training/toggle-play product]
                                                    [:asr-training/select-utterance product utterance]))
                         :on-seek #(re/dispatch (if selected?
                                                  [:asr-training/seek product %]
                                                  [:asr-training/select-utterance product utterance]))}])))

(defn list-transcript-input [_]
  (let [focus! #(some-> (re/dom-node %) (.querySelector "textarea") (.focus))]
    (re/create-class
      {:component-did-update (fn [this [_ prev-props]]
                               (when (and (:selected? (re/props this)) (not (:selected? prev-props)))
                                 (focus! this)))
       :reagent-render
       (fn [{:keys [product utterance]}]
         (let [k (asr-training.d/utterance-key utterance)
               default @(re/subscribe [:asr-training/default-transcript- product (:transcript utterance)])]
           [:div {:style {:flex 1
                          :min-width 0}}
            ^{:key (str k "-" @(re/subscribe [:asr-training/reset-count- product]) "-" default)}
            [alc/up-textarea {:default-value (or @(re/subscribe [:asr-training/edit- product k]) default)
                              :rows 1
                              :style {:min-height :auto}
                              :on-change #(re/dispatch [:asr-training/edit-utterance product utterance (.. % -target -value)])
                              :on-key-down (transcript-key-down product utterance)}]]))})))

(defn list-actions [{:keys [product utterance selected?]}]
  (let [{:keys [transcript]} utterance
        edit @(re/subscribe [:asr-training/edit- product (asr-training.d/utterance-key utterance)])
        default @(re/subscribe [:asr-training/default-transcript- product transcript])
        error? (when selected?
                 (:error? @(re/subscribe [:asr-training/audio- product])))
        changed? (not= (or edit default) transcript)
        {:keys [label icon-type button-style]} (submit-config changed?)]
    [:div {:style {:display :flex
                   :align-items :center
                   :gap 8}}
     [alc/up-button {:icon "md"
                     :style button-style
                     :disabled (and (asr-training.d/listening-required?)
                                    (not (and selected? (or error? (asr-training.d/listened-enough? utterance)))))
                     :tooltip-content (shortcut-tooltip label cmd-key "Enter")
                     :on-click #(re/dispatch [:asr-training/submit-utterance product utterance])}
      [alc/up-icon {:icon-type icon-type
                    :style {:color (:color button-style)
                            :opacity 1}}]]
     [alc/up-button {:icon-type :DoubleChevronRightIcon
                     :tooltip-content (shortcut-tooltip "Skip" cmd-key "J")
                     :on-click #(re/dispatch [:asr-training/skip-utterance product utterance])}]
     [alc/up-button {:icon-type :UndoIcon
                     :disabled (not changed?)
                     :tooltip-content "Reset"
                     :on-click #(re/dispatch [:asr-training/reset-utterance product utterance])}]]))

(defn list-table [{:keys [product]}]
  (let [utterances- (re/subscribe [:asr-training/utterances- product])
        audio- (re/subscribe [:asr-training/audio- product])]
    (re/create-class
      {:component-did-mount #(re/dispatch [:asr-training/prefetch-audio product nil])
       :reagent-render
       (fn [{:keys [product]}]
         (let [selected-key (some-> @audio- :utterance (asr-training.d/utterance-key))
               cell-style {:padding "0 12px"}]
           [alc/table (merge (table-args product)
                             {:flex-vals ["0 0 260px" 1 1 "0 0 160px"]
                              :headers ["Audio" "Original Transcript" "Corrected Transcript" ""]
                              :rows (map
                                      (fn [{:keys [transcript] :as utterance}]
                                        (let [k (asr-training.d/utterance-key utterance)
                                              row-args {:product product
                                                        :utterance utterance
                                                        :selected? (= selected-key k)}]
                                          {:key k
                                           :cells [{:label [list-player row-args]
                                                    :style cell-style}
                                                   {:label transcript
                                                    :style cell-style}
                                                   {:label [list-transcript-input row-args]
                                                    :style cell-style}
                                                   {:label [list-actions row-args]
                                                    :style cell-style}]}))
                                      @utterances-)})]))})))

(defn utterance-player [{:keys [product]}]
  (let [audio- (re/subscribe [:asr-training/audio- product])
        edits- (re/subscribe [:asr-training/edits- product])
        reset-count- (re/subscribe [:asr-training/reset-count- product])
        playback-speed- (re/subscribe [:asr-training/playback-speed-])]
    (fn [{:keys [product]}]
      (let [{:keys [utterance loading? playing? error?]} @audio-
            {:keys [transcript]} utterance
            k (asr-training.d/utterance-key utterance)
            disabled? (or loading? error?)
            default @(re/subscribe [:asr-training/default-transcript- product transcript])
            changed? (not= (get @edits- k default) transcript)]
        [:div {:style {:display :flex
                       :flex-direction :column
                       :gap 12}}
         (when-not @(re/subscribe [:asr-training/hide-original-transcript?-])
           [:span {:style {:color (style/alc-color "Text/primary")
                           :font-size 14
                           :line-height 1.43}}
            (str "Original Transcript: " transcript)])
         [audio/player-bar {:playing? playing?
                            :loading? loading?
                            :disabled? disabled?
                            :position @asr-training.d/position*
                            :duration (asr-training.d/segment-duration utterance)
                            :on-toggle #(re/dispatch [:asr-training/toggle-play product])
                            :on-seek #(re/dispatch [:asr-training/seek product %])
                            :on-play #(re/dispatch [:asr-training/play product])
                            :playback-speed @playback-speed-
                            :on-playback-speed-change #(re/dispatch [:asr-training/set-playback-speed product %])
                            :waveform {:buffer (when-not disabled? @asr-training.d/audio-buffer*)
                                       :start (-> utterance :start (/ 1000))
                                       :end (-> utterance :end (/ 1000))}}]
         (when error?
           [:span {:style {:color (style/alc-color "Text/secondary")
                           :font-size 12}}
            "Audio unavailable"])
         [:div {:style {:display :flex
                        :flex-direction :column
                        :gap 6
                        :margin-top 18}}
          ^{:key (str k "-" @reset-count- "-" default)}
          [alc/up-textarea {:default-value (get @edits- k default)
                            :auto-focus true
                            :rows 6
                            :on-change #(re/dispatch [:asr-training/edit-utterance product utterance (.. % -target -value)])
                            :on-key-down (transcript-key-down product utterance)}]
          (when-let [replacement (and changed? @(re/subscribe [:asr-training/replacement- product]))]
            [:span {:style {:color (style/alc-color "Text/secondary")
                            :font-size 12
                            :line-height 1.33}}
             (str "Auto-replaced \"" @(re/subscribe [:asr-training/search-]) "\" with \"" replacement "\"")])]
         (let [{:keys [label icon-type button-style]} (submit-config changed?)]
           [:div {:style {:display :flex
                          :align-items :center
                          :gap 12}}
            [alc/up-button {:style (assoc button-style :padding-inline-start 12)
                            :disabled (not (or error? (asr-training.d/listened-enough? utterance)))
                            :tooltip-content (shortcut-tooltip nil cmd-key "Enter")
                            :on-click #(re/dispatch [:asr-training/submit-utterance product utterance])}
             [alc/up-icon {:icon-type icon-type
                           :style {:color (:color button-style)
                                   :opacity 1}}]
             label]
            [alc/up-button {:icon-type :DoubleChevronRightIcon
                            :tooltip-content (shortcut-tooltip nil cmd-key "J")
                            :on-click #(re/dispatch [:asr-training/skip-utterance product utterance])}
             "Skip"]
            [c/spacer]
            [alc/up-button {:disabled (not changed?)
                            :on-click #(re/dispatch [:asr-training/reset-utterance product utterance])}
             "Reset"]])]))))

(defn start-training [{:keys [product utterances]}]
  (let [loaded? (sequential? utterances)
        first-with-audio (when loaded?
                           (->> utterances (filter :audio-url) (first)))]
    [:div {:style {:flex 1
                   :display :flex
                   :align-items :center
                   :justify-content :center
                   :padding 16}}
     (if (and loaded? (not first-with-audio))
       [empty-state/no-data-card {:material-icon-type :volume-off
                                  :text "No Audio Available"}]
       [alc/purple-button {:size :large
                           :disabled? (not loaded?)
                           :on-click #(do
                                        (re/dispatch [:asr-training/select-utterance product first-with-audio])
                                        (re/dispatch [:layout/expand-panel-right]))}
        "Start Training"])]))

(defmethod l/panel-right-view
  asr-training.d/panel-key [_ {:keys [product]}]
  (let [audio @(re/subscribe [:asr-training/audio- product])
        utterances @(re/subscribe [:asr-training/utterances- product])
        expanded? @(re/subscribe [:layout/panel-right-expanded?-])]
    [:div {:style {:display :flex
                   :flex-direction :column
                   :height "100%"}}
     [:div {:style {:background-color (style/alc-color "Background/secondary")
                    :display :flex
                    :align-items :center
                    :gap 8
                    :padding "12px 16px"
                    :min-height 58
                    :box-sizing :border-box}}
      [:span {:style {:color (style/alc-color "Text/primary_header")
                      :font-size 20
                      :font-weight 400
                      :line-height 1.4}}
       "Utterance"]
      [c/spacer]
      [alc/up-button {:icon-type (if expanded? :MinimizeIcon :ZoomExpandIcon)
                      :rounded? true
                      :tooltip-content (if expanded? "Minimize" "Maximize")
                      :on-click #(re/dispatch [:layout/toggle-panel-right-expanded])}]]
     (if (:utterance audio)
       [:div {:style {:padding 16}}
        [utterance-player {:product product}]]
       [start-training {:product product
                        :utterances utterances}])]))

(defmethod alc/dialog-view
  :asr-training/settings-dialog [_ {:keys [product]}]
  (let [checkbox-row (fn [form-name label]
                       [:label {:style {:align-items :center
                                        :color (style/alc-color "Text/primary")
                                        :cursor :pointer
                                        :display :flex
                                        :font-size 13
                                        :gap 8}}
                        [alc/up-checkbox {:form-path asr-training.d/settings-form-path
                                          :form-name form-name}]
                        label])]
    [alc/dialog {:title "ASR Training Settings"
                 :footer [:<>
                          [alc/up-call-to-action-button {:on-click #(re/dispatch [:asr-training/save-settings product])}
                           "Save"]
                          [alc/up-button {:on-click #(re/dispatch [:dialog/hide])}
                           "Cancel"]]}
     (if (= asr-training.d/list-mode @(re/subscribe [:asr-training/mode-]))
       [:div {:style {:display :flex
                      :flex-direction :column
                      :gap 16
                      :width 480}}
        [:div {:style {:display :flex
                       :flex-direction :column
                       :gap 8}}
         [:span {:style {:color (style/alc-color "Text/primary_header")
                         :font-size 14
                         :font-weight 500}}
          "Playback Speed"]
         [alc/up-dropdown {:form-path asr-training.d/settings-form-path
                           :form-name :playback-speed
                           :options audio/playback-speed-options}]]
        (checkbox-row :auto-play? "Auto-play the next utterance after submitting or skipping")]
       [:div {:style {:display :flex
                      :flex-direction :column
                      :gap 16
                      :width 480}}
        (checkbox-row :hide-original-transcript? "Hide Original Transcript in Utterance Panel")])]))

(defmethod alc/dialog-view
  :asr-training/replace-dialog [_ {:keys [product]}]
  (let [replace-with @(re/subscribe [:form/val- asr-training.d/replace-form-path :replacement])
        label-style {:color (style/alc-color "Text/primary_header")
                     :font-size 14
                     :font-weight 500}]
    [alc/dialog {:title "Replace Search Matches"
                 :footer [:<>
                          [alc/up-call-to-action-button {:disabled (s/blank? replace-with)
                                                         :on-click #(re/dispatch [:asr-training/save-replacement product])}
                           "Replace"]
                          (when @(re/subscribe [:asr-training/replacement- product])
                            [alc/up-button {:on-click #(re/dispatch [:asr-training/clear-replacement product])}
                             "Remove"])
                          [alc/up-button {:on-click #(re/dispatch [:dialog/hide])}
                           "Cancel"]]}
     [:div {:style {:display :flex
                    :flex-direction :column
                    :gap 16
                    :width 480}}
      [:div {:style {:display :flex
                     :flex-direction :column
                     :gap 8}}
       [:span {:style label-style}
        "Search"]
       [alc/up-input {:default-value @(re/subscribe [:asr-training/search-])
                      :disabled true}]]
      [:div {:style {:display :flex
                     :flex-direction :column
                     :gap 8}}
       [:span {:style label-style}
        "Replace With"]
       [alc/up-input {:form-path asr-training.d/replace-form-path
                      :form-name :replacement
                      :placeholder "Replacement"
                      :auto-focus true
                      :on-key-down #(when (= "Enter" (.-key %))
                                      (re/dispatch [:asr-training/save-replacement product]))}]]
      [:span {:style {:color (style/alc-color "Text/secondary")
                      :font-size 12
                      :line-height 1.33}}
       "Matches ignore casing. Corrected transcripts are pre-populated with the replacement until the search changes."]]]))

(defn mode-toggle [{:keys [product]}]
  [alc/up-toggle-group {:type "single"
                        :value @(re/subscribe [:asr-training/mode-])
                        :on-value-change #(when (not-empty %)
                                            (re/dispatch [:asr-training/set-mode product %]))
                        :items [{:value asr-training.d/list-mode
                                 :content "List Mode"}
                                {:value asr-training.d/utterance-mode
                                 :content "Utterance Mode"}]
                        :style {"--spacing" "0.225rem"}}])

(defn header [{:keys [product]}]
  (let [observer* (atom nil)
        search- (re/subscribe [:asr-training/search-])
        replacement- (re/subscribe [:asr-training/replacement- product])
        set-search (util/debounce #(re/dispatch [:asr-training/set-search product %]) 750)]
    (re/create-class
      {:component-did-mount (fn [this]
                              (let [el (re/dom-node this)
                                    observer (js/ResizeObserver.
                                               #(re/dispatch [:asr-training/set-header-height product
                                                              (.-height (.getBoundingClientRect el))]))]
                                (.observe observer el)
                                (reset! observer* observer)))
       :component-will-unmount #(some-> ^js @observer* (.disconnect))
       :reagent-render
       (fn [{:keys [product]}]
         [:div {:style {:background-color (style/alc-color "Background/card_hex")
                        :display :flex
                        :flex-direction :column
                        :gap 16
                        :padding "12px 16px 16px"
                        :position :sticky
                        :top 0
                        :z-index (inc (style/z :medium))}}
          [:div {:style {:display :flex
                         :align-items :flex-start
                         :justify-content :space-between}}
           [:span {:style {:color (style/alc-color "Text/primary_header")
                           :font-size 24
                           :line-height "36px"}}
            "ASR Training (NOT FUNCTIONAL YET)"]
           [:div {:style {:display :flex
                          :align-items :flex-start
                          :gap 8}}
            [alc/up-button {:icon-type :SettingsIcon
                            :tooltip-content "Settings"
                            :on-click #(re/dispatch [:asr-training/open-settings product])}]
            [so.filters/filter-button {:config (asr-training.d/filters-config product)}]
            [:div {:style {:display :flex
                           :flex-direction :column
                           :gap 4
                           :width 240}}
             [alc/up-input {:placeholder "Search Utterance Transcripts"
                            :default-value @search-
                            :on-change #(set-search (.. % -target -value))}]
             (when-let [replacement @replacement-]
               [:span {:style {:color (style/alc-color "Text/secondary")
                               :font-size 12
                               :line-height 1.33}}
                (str "Auto-replaced by \"" replacement "\"")])]
            [alc/up-button {:icon-type :ReplaceIcon
                            :disabled (nil? @search-)
                            :tooltip-content "Replace"
                            :on-click #(re/dispatch [:asr-training/open-replace product])}]]]
          [so.filters/applied-filter-pills {:config (asr-training.d/filters-config product)}]])})))

(defn root [{:keys [product]}]
  (let [loading?- (re/subscribe [:asr-training/list-loading?- product])
        utterances- (re/subscribe [:asr-training/utterances- product])
        mode- (re/subscribe [:asr-training/mode-])
        cur-route- (re/subscribe [:router/current-route-])
        layout (get {"smart-ordering" l/logged-in-with-header-nav-smart-ordering} product)]
    (re/create-class
      {:component-did-mount #(re/dispatch [:asr-training/initialize product])
       :component-will-unmount #(re/dispatch [:asr-training/teardown product])
       :reagent-render
       (fn [args]
         [layout
          (merge (dissoc args :product) {:concept-dropdown? true
                                         :location-dropdown? true
                                         :header-contents [mode-toggle {:product product}]
                                         :concept-dropdown-config
                                         {:extra-on-change
                                          (fn [value]
                                            (let [cid (when (not (s/blank? value))
                                                        (util/parse-int value))
                                                  [route-name route-params route-query] @cur-route-]
                                              (re/dispatch [:locations/clear-selected])
                                              (re/dispatch [:router/go [route-name
                                                                        route-params
                                                                        (cond-> (dissoc (or route-query {}) :lid)
                                                                                cid (assoc :cid cid)
                                                                                (not cid) (dissoc :cid))]])))}})
          [:div {:style {:display :flex
                         :flex-direction :column
                         :width "100%"
                         :min-height "100%"}}
           [header {:product product}]
           [:div {:style {:flex 1
                          :display :flex
                          :flex-direction :column
                          :padding "0 16px 12px"}}
            (cond
              (or @loading?- (util/loading? @utterances-)) [empty-state/loading-utterances-card]
              (empty? @utterances-) [empty-state/no-utterances-card]
              (= asr-training.d/list-mode @mode-) [list-table {:product product}]
              :else [utterances-table {:product product}])]]])})))
